// src/pages/Video.jsx
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot } from 'firebase/firestore';
import toast from 'react-hot-toast';

import { db } from '../firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { captureRef } from '../utils/referral.js';
import DownloadConsent from '../components/DownloadConsent.jsx';
import ProgressBar from '../components/ProgressBar.jsx';
import SEO from '../components/SEO.jsx';

export default function Video() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [video, setVideo] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // 공유받은 링크의 ?ref=CODE 를 보관 → 이후 가입 시 자동 적용
  useEffect(() => { captureRef(); }, []);

  useEffect(() => {
    if (!id) return undefined;
    const unsub = onSnapshot(
      doc(db, 'videos', id),
      (snap) => {
        if (!snap.exists()) setNotFound(true);
        else setVideo({ id: snap.id, ...snap.data() });
      },
      () => setNotFound(true),
    );
    return unsub;
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500">
        {t('video.notFound')}
      </div>
    );
  }
  if (!video) {
    return <div className="text-center py-12 text-gray-500">{t('account.loading')}</div>;
  }

  const status = video.renderStatus;
  const isPending = status === 'pending' || status === 'rendering';
  const isDone = status === 'done' && video.videoUrl;
  const isFailed = status === 'failed';
  const isOwner = user?.uid === video.uid;

  async function handleCopyHashtags() {
    const tags = (video.script?.hashtags || []).join(' ');
    try {
      await navigator.clipboard.writeText(tags);
      toast.success(t('account.copied'));
    } catch {
      toast.error(t('account.copyFailed'));
    }
  }

  // 공유 URL — 로그인 사용자면 본인 레퍼럴 코드를 자동으로 붙인다.
  // (현재 URL 에 남의 ref 가 있을 수 있으므로 깨끗한 경로 + 본인 코드로 새로 조립)
  function buildShareUrl() {
    const base = `${window.location.origin}/video/${id}`;
    const code = profile?.referralCode;
    return code ? `${base}?ref=${encodeURIComponent(code)}` : base;
  }

  async function handleShare() {
    const shareUrl = buildShareUrl();
    const shareData = {
      title: video.script?.title || 'CitizenCast',
      text: (video.script?.hashtags || []).join(' '),
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {/* user cancelled */}
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('account.copied'));
      } catch {
        toast.error(t('account.copyFailed'));
      }
    }
  }

  async function handleCopyRefLink() {
    try {
      await navigator.clipboard.writeText(buildShareUrl());
      toast.success(t('account.copied'));
    } catch {
      toast.error(t('account.copyFailed'));
    }
  }

  return (
    <div className="max-w-md mx-auto px-4 py-6 space-y-4">
      <SEO
        title={video?.script?.title || video?.newsTitle || t('video.defaultTitle')}
        description={video?.script?.title ? t('video.seoDescWithTitle', { title: video.script.title }) : t('video.seoDescDefault')}
        path={`/video/${id}`}
        image={video?.videoUrl ? undefined : undefined}
      />
      {isPending && isOwner && (
        <div className="card text-center space-y-4">
          <div className="text-3xl">⏳</div>
          <div className="font-bold">{t('video.preparing')}</div>
          <ProgressBar percent={status === 'rendering' ? 90 : 30} labelKey="rendering" />
          <p className="text-xs text-gray-500">
            {t('video.autoRefresh')}
          </p>
        </div>
      )}

      {isPending && !isOwner && (
        <div className="card text-center space-y-2">
          <div className="text-3xl">⏳</div>
          <p className="text-sm text-gray-500">{t('video.notReady')}</p>
        </div>
      )}

      {isDone && (
        <>
          <div className="rounded-2xl overflow-hidden bg-black aspect-[9/16] max-w-sm mx-auto">
            <video
              controls
              playsInline
              className="w-full h-full object-contain"
              src={video.videoUrl}
            />
          </div>

          {video.script?.title && (
            <h1 className="text-xl font-bold text-center">{video.script.title}</h1>
          )}

          {video.script?.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center">
              {video.script.hashtags.map((tag) => (
                <span key={tag} className="badge bg-gray-100">
                  {tag.startsWith('#') ? tag : `#${tag}`}
                </span>
              ))}
              <button
                onClick={handleCopyHashtags}
                className="badge bg-white border"
                title={t('video.copyHashtags')}
              >
                📋
              </button>
            </div>
          )}

          {profile?.referralCode && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-center space-y-2">
              <p className="text-sm font-bold text-amber-900">🎁 {t('video.referralHint')}</p>
              <button
                onClick={handleCopyRefLink}
                className="text-xs font-mono bg-white border rounded-lg px-3 py-1.5 hover:bg-gray-50"
                title={t('account.copyLinkTitle')}
              >
                {t('video.refCode', { code: profile.referralCode })} 📋
              </button>
            </div>
          )}

          {isOwner ? (
            <>
              <DownloadConsent videoId={video.id} videoUrl={video.videoUrl} />
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => navigate('/create')} className="btn-secondary">
                  {t('video.newVideoBtn')}
                </button>
                <button onClick={handleShare} className="btn-secondary">
                  {t('video.shareBtn')}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <button onClick={handleShare} className="btn-secondary w-full">
                {t('video.shareBtn')}
              </button>
              <Link to="/register" className="btn-primary w-full inline-block text-center">
                {t('video.makeMyOwn')}
              </Link>
            </div>
          )}
        </>
      )}

      {isFailed && !isOwner && (
        <div className="card text-center space-y-2">
          <div className="text-3xl">😵</div>
          <p className="text-sm text-gray-500">{t('video.notAvailable')}</p>
          <Link to="/register" className="btn-primary inline-block">{t('video.makeMyOwn')}</Link>
        </div>
      )}

      {isFailed && isOwner && (
        <div className="card space-y-3 text-center">
          <div className="text-3xl">😵</div>
          <div className="font-bold">{t('errors.renderFailed')}</div>
          {video.renderError && (
            <p className="text-xs text-gray-500">{video.renderError}</p>
          )}
          <Link to="/create" className="btn-primary inline-block">
            {t('video.tryAgain')}
          </Link>
        </div>
      )}
    </div>
  );
}
